/**
 * Results Step
 * 
 * Display import results and summary
 */

import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as NewImportIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';

interface ResultsStepProps {
  importResult: any;
  onClose: () => void;
  onStartNew: () => void;
}

const ResultsStep: React.FC<ResultsStepProps> = ({
  importResult,
  onClose,
  onStartNew
}) => {
  if (!importResult) {
    return (
      <Alert severity="error">
        No import results available.
      </Alert>
    );
  }

  const {
    success,
    imported_count,
    failed_count,
    skipped_count,
    total_rows,
    errors
  } = importResult;

  const hasErrors = errors && errors.length > 0;
  const allImported = imported_count === total_rows;

  return (
    <Box>
      {/* Success/Failure Header */}
      {allImported ? (
        <Box textAlign="center" mb={4}>
          <SuccessIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Import Successful!
          </Typography>
          <Typography variant="body1" color="textSecondary">
            All {imported_count} rows have been imported successfully.
          </Typography>
        </Box>
      ) : (
        <Box textAlign="center" mb={4}>
          <WarningIcon sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Import Completed with Issues
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {imported_count} of {total_rows} rows were imported successfully.
          </Typography>
        </Box>
      )}

      {/* Summary Cards */}
      <Box display="flex" gap={2} mb={3}>
        <Paper sx={{ p: 3, flex: 1, bgcolor: 'success.lighter', textAlign: 'center' }}>
          <SuccessIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="h3">{imported_count}</Typography>
          <Typography variant="body2">Imported</Typography>
        </Paper>

        {failed_count > 0 && (
          <Paper sx={{ p: 3, flex: 1, bgcolor: 'error.lighter', textAlign: 'center' }}>
            <ErrorIcon color="error" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h3">{failed_count}</Typography>
            <Typography variant="body2">Failed</Typography>
          </Paper>
        )}

        {skipped_count > 0 && (
          <Paper sx={{ p: 3, flex: 1, bgcolor: 'warning.lighter', textAlign: 'center' }}>
            <WarningIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h3">{skipped_count}</Typography>
            <Typography variant="body2">Skipped</Typography>
          </Paper>
        )}
      </Box>

      {/* Detailed Summary */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Import Summary
        </Typography>
        
        <Box display="flex" flexDirection="column" gap={1} mt={2}>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2">Total Rows Processed:</Typography>
            <Chip label={total_rows} size="small" />
          </Box>
          
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="success.main">
              Successfully Imported:
            </Typography>
            <Chip label={imported_count} color="success" size="small" />
          </Box>
          
          {failed_count > 0 && (
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="error.main">
                Failed to Import:
              </Typography>
              <Chip label={failed_count} color="error" size="small" />
            </Box>
          )}
          
          {skipped_count > 0 && (
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="warning.main">
                Skipped (Invalid):
              </Typography>
              <Chip label={skipped_count} color="warning" size="small" />
            </Box>
          )}
          
          <Box display="flex" justifyContent="space-between" mt={1} pt={1} borderTop="1px solid" borderColor="divider">
            <Typography variant="body2">
              <strong>Success Rate:</strong>
            </Typography>
            <Typography variant="body2">
              <strong>{((imported_count / total_rows) * 100).toFixed(1)}%</strong>
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Errors Table */}
      {hasErrors && (
        <Box mb={3}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>{errors.length} errors occurred during import.</strong> 
            Review the details below.
          </Alert>
          
          <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell width="20%"><strong>Error</strong></TableCell>
                  <TableCell width="80%"><strong>Details</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errors.map((error: string, index: number) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Chip label={`Error ${index + 1}`} color="error" size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{error}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Next Steps */}
      <Box sx={{ p: 2, bgcolor: 'info.lighter', borderRadius: 1, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          ✅ What's next:
        </Typography>
        <Typography variant="body2" component="div">
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>Your data has been added to the system</li>
            <li>You can view and manage the imported records in their respective sections</li>
            {(failed_count > 0 || skipped_count > 0) && (
              <li>Fix the failed/skipped rows in your file and import them again</li>
            )}
            <li>You can import more data anytime using the Import button</li>
          </ul>
        </Typography>
      </Box>

      {/* Action Buttons */}
      <Box display="flex" justifyContent="space-between" gap={2}>
        <Button
          variant="outlined"
          onClick={onStartNew}
          startIcon={<NewImportIcon />}
        >
          Import More Data
        </Button>
        <Button
          variant="contained"
          onClick={onClose}
          startIcon={<ViewIcon />}
        >
          View Imported Data
        </Button>
      </Box>
    </Box>
  );
};

export default ResultsStep;

